import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DashboardLayout } from '@/components/florist-dashboard/DashboardLayout';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Info } from 'lucide-react';

const PaymentSettings = () => {
  const { user, supabase } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [storeProfile, setStoreProfile] = useState<any>(null);
  const [stripeAccount, setStripeAccount] = useState<any>(null);
  
  const fetchProfileAndStripeAccount = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data: profileData, error: profileError } = await (supabase
      .from('store_profiles') as any)
      .select('id, store_name, stripe_account_id') // ✅ FIXED: Include stripe_account_id in query
      .eq('florist_id', user.id)
        .single();

    if (profileError) {
      if (profileError.code !== 'PGRST116') {
        console.error('Error fetching store profile:', profileError);
        toast.error('Failed to load store profile.');
      }
      setLoading(false);
      return;
    }
    
    setStoreProfile(profileData);
      
    if (profileData?.stripe_account_id) {
      // ✅ FIXED: Use stripe_account_id directly from store_profiles instead of separate table
      setStripeAccount({
        stripe_user_id: profileData.stripe_account_id
      });
    } else {
      setStripeAccount(null);
    }
    
    setLoading(false);
  };

  useEffect(() => {
    fetchProfileAndStripeAccount();
  }, [user, supabase]);

  const handleDisconnectStripe = async () => {
    if (!stripeAccount?.stripe_user_id) {
        toast.error("Stripe account not found or already disconnected.");
      return;
    }

    if (!confirm("Are you sure you want to disconnect your Stripe account? This will prevent you from receiving new payments.")) {
      return;
    }

    toast.info("Disconnecting from Stripe...");
    
    try {
      // Simply remove the Stripe account ID from the store profile
      const { error } = await supabase
        .from('store_profiles')
        .update({ stripe_account_id: null })
        .eq('florist_id', user?.id);
        
      if (error) throw error;
      
      toast.success("Stripe account disconnected successfully.");
      // Refetch data to update the UI
      fetchProfileAndStripeAccount();
    } catch (error: any) {
      console.error('Error disconnecting Stripe:', error);
      toast.error(`Failed to disconnect Stripe: ${error.message}`);
    }
  };

  const openStripeDashboard = () => {
    if (stripeAccount?.stripe_user_id) {
        // stripe_user_id already includes 'acct_' prefix
        const stripeDashboardUrl = `https://connect.stripe.com/app/express#${stripeAccount?.stripe_user_id}/overview`;
      window.open(stripeDashboardUrl, '_blank');
    } else {
      toast.error("Stripe account ID not found.");
    }
  };
  
  const renderContent = () => {
    if (loading) {
      return <div>Loading payment information...</div>;
    }

    if (!storeProfile) {
      return (
          <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Store Profile Not Found</h2>
              <p className="text-gray-600">A store profile is required to manage payment settings. Please complete your profile first.</p>
          </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Payment Processing</h2>
        <p className="text-gray-600 mb-6">
          Manage your Stripe connection to start accepting payments for your flower orders.
        </p>
        
        {stripeAccount?.stripe_user_id ? (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="font-semibold text-green-800">✓ Stripe Account Connected</p>
            <p className="text-sm text-green-600 mt-1">Account ID: {stripeAccount.stripe_user_id}</p>
            <div className="mt-4 flex gap-4">
                <Button variant="outline" onClick={openStripeDashboard}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Stripe Dashboard
                    </Button>
                <Button variant="destructive" onClick={handleDisconnectStripe}>
                      Disconnect Stripe
                    </Button>
                  </div>
                </div>
              ) : (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-gray-600">Stripe account is not connected.</p>
            <Button 
              className="mt-2" 
              onClick={async () => {
                try {
                  console.log('🔍 Starting Stripe Connect for user:', user?.id);
                  
                  const response = await fetch('/api/stripe/connect', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ floristId: user?.id })
                  });
                  
                  console.log('🔍 API Response status:', response.status);
                  console.log('🔍 API Response headers:', response.headers);
                  
                  if (!response.ok) {
                    const errorText = await response.text();
                    console.error('🔍 API Error response:', errorText);
                    toast.error(`API Error: ${response.status} - ${errorText.substring(0, 100)}`);
                    return;
                  }
                  
                  const data = await response.json();
                  console.log('🔍 API Response data:', data);
                  
                  if (data.url) {
                    console.log('🔍 Redirecting to Stripe URL:', data.url);
                    window.location.href = data.url;
                  } else {
                    console.error('🔍 No URL in response:', data);
                    toast.error('No redirect URL received from Stripe API');
                  }
                } catch (error) {
                  console.error('🔍 Stripe connect error:', error);
                  toast.error(`Connection failed: ${error.message}`);
                }
              }}
            >
              Connect with Stripe
            </Button>
                </div>
              )}

        <div className="mt-8">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>
                    <div className="flex items-center gap-2">
                        <Info className="h-5 w-5 text-gray-600" />
                        <span className="font-semibold text-lg">How It Works</span>
                    </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 text-gray-700 space-y-4">
                  <div>
                    <h4 className="font-semibold text-md mb-2">1. Connecting Your Account (One-Time Setup)</h4>
                    <p className="text-sm">To receive payments, you first link your bank account via Stripe's secure "Connect" feature. You'll be taken to Stripe's website to log in or create an account and provide your business details. MarketBloom never sees or stores your sensitive financial information.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-md mb-2">2. Receiving an Order</h4>
                    <p className="text-sm">When a customer pays, the funds are processed and held securely by Stripe. You get the order notification as usual, and can see the order total and commission details.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-md mb-2">3. Getting Paid (Payouts)</h4>
                    <p className="text-sm">You don't have to do anything! After an order is completed, Stripe automatically calculates your share and deposits it directly into your connected bank account, typically within 2-7 business days.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-md mb-2">4. Managing Your Payments & Refunds</h4>
                    <p className="text-sm">The "Open Stripe Dashboard" button gives you access to a world-class financial dashboard. There you can view all payments, track upcoming payouts, update your bank details, and handle any refunds or disputes directly.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-md mb-2">5. Disconnecting Your Account</h4>
                    <p className="text-sm">If you need to stop selling, the "Disconnect Stripe" button will securely revoke our permission to process payments for you. This will not affect any payouts for orders already processed.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
                </div>
      </div>
  );
  };

  return (
    <DashboardLayout>
      <h1 className="text-3xl font-bold mb-8">Payment Settings</h1>
      {renderContent()}
    </DashboardLayout>
  );
};

export default PaymentSettings; 